// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListActions from "@keller/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@keller/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@keller/Overrides/Widgets/ProductList/ProductListProductInformation";
import { constants } from "@keller/XDL/constants";
import { xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

// XNG-Changes: add showReorderPadProductAttributes option for ReorderPad page
type Props = ProductCardSelections & HasProduct & { showReorderPadProductAttributes?: boolean };

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    container?: InjectableCss;
}

const { productGridView: productGridViewScreen } = constants;
export const productGridCardStyles: ProductListProductGridCardStyles = {
    container: {
        css: css`
            width: 100%;
            max-width: ${productGridViewScreen.desktop.width}px;
            ${xScreen("lte", "md")} {
                max-width: ${productGridViewScreen.tablet.width}px;
            }

            ${xScreen("lte", "xs")} {
                margin-left: 0;
                max-width: 100%;
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            margin: 0;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 12px;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    showReorderPadProductAttributes,
}) => {
    // XNG-Changes: Update attributes
    return (
        <StyledWrapper {...styles.container} data-test-selector={`productListProductCard${product.id}`}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.imageWrapper}>
                    {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                    <ProductListProductImage
                        showImage={showImage} 
                        showCompare={false} 
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                        view="Grid" />
                </StyledWrapper>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={false}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={showTextBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                    showPreviousPurchase={false}
                    showReorderPadProductAttributes={showReorderPadProductAttributes}
                    view="Grid"
                />
                <ProductListActions
                    showAddToList={showAddToList}
                    showAvailability={showAvailability}
                    showPreviousPurchase={true}
                    showPrice={showPrice}
                    showCompare={showCompare}
                    view="Grid"
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductGridCard);
